import React, { useState } from 'react';

import { Table, NavLinkButton } from '@shared/components/bootstrap';

import { Panel } from '@admin/components/helpers/panel';
import { Pagination } from '@admin/components/pagination';
import { getWarehouseOrDepotInput } from '@admin/components/shifts/utilities';
import { Spinner } from '@admin/components/spinner';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import {
  useHiringJobApplicationsPaginatedQuery,
  Hiring__JobApplicationStatusEnum,
  HiringJobListingFragment,
  Currency,
} from '@admin/schema';

import { Filters, FormValues } from './filters';
import { getStatusBadgeClass, getStatusDisplayText } from './status';

const JobListingInfo: React.FC<{ jobListing: HiringJobListingFragment }> = ({ jobListing }) => (
  <>
    <div>
      <strong>{jobListing.job.title}</strong>
    </div>
    <div>{jobListing.warehouseOrDepot.name}</div>
    <div>
      {jobListing.fullTime ? 'Full-Time' : 'Part-Time'}
      {jobListing.nightShift ? ' • Night Shift' : ''}
    </div>
    <div>
      {jobListing.hourlyRate.toLocaleString('en-US', {
        style: 'currency',
        currency: jobListing.region.currency === Currency.Usd ? 'USD' : 'CAD',
      })}
      /hour
    </div>
  </>
);

export const HiringJobApplications: React.FC = () => {
  useTitle('Hiring | Candidates');
  const [page, setPage] = useState<number | undefined>();
  const [formValues, setFormValues] = useState<FormValues>({});

  const { data, loading } = useHiringJobApplicationsPaginatedQuery({
    client,
    variables: {
      page,
      candidateSearch: formValues.candidateSearch,
      status: formValues.status as Hiring__JobApplicationStatusEnum,
      jobID: formValues.jobID,
      regionID: formValues.regionID,
      warehouseOrDepot: formValues.encodedWarehouseOrDepot
        ? getWarehouseOrDepotInput(formValues.encodedWarehouseOrDepot)
        : undefined,
      interviewDate: formValues.interviewDate,
    },
  });

  const paginated = data?.paginated;

  const updatedFilters = (formikFormValues: FormValues) => {
    setFormValues(formikFormValues);
    setPage(undefined); // Reset to first page when filtering

    // refetch();
  };

  return (
    <>
      <Filters filters={formValues} onChangeFilters={updatedFilters} />

      <Panel>
        <Table responsive striped>
          <thead>
            <tr>
              <th>Candidate</th>
              <th>Job Listing</th>
              <th>Interview</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {paginated?.results.map((application) => (
              <tr key={application.id}>
                <td>
                  <div>
                    <strong>{application.candidate.name}</strong>
                  </div>
                  <div>{application.candidate.email}</div>
                  <div>{application.candidate.phone}</div>
                </td>
                <td>
                  {application.hiredJobListing && application.hiredJobListing.id !== application.jobListing.id ? (
                    <>
                      <strong>Applied</strong>
                      <JobListingInfo jobListing={application.jobListing} />

                      <hr />

                      <strong>Offered</strong>
                      <JobListingInfo jobListing={application.hiredJobListing} />
                    </>
                  ) : (
                    <JobListingInfo jobListing={application.jobListing} />
                  )}
                </td>
                <td>
                  {application.interviews.length > 0 ? (
                    application.interviews.map((interview, index) => (
                      <div key={interview.id}>
                        <div>
                          <div>
                            <strong>
                              {new Date(interview.scheduledStart).toLocaleDateString('en-US', {
                                timeZone: interview.warehouseOrDepot.tz,
                                month: 'long',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </strong>
                          </div>
                          <div>
                            {new Date(interview.scheduledStart).toLocaleTimeString('en-US', {
                              timeZone: interview.warehouseOrDepot.tz,
                              hour: 'numeric',
                              minute: '2-digit',
                            }) +
                              ' - ' +
                              new Date(interview.scheduledEnd).toLocaleTimeString('en-US', {
                                timeZone: interview.warehouseOrDepot.tz,
                                hour: 'numeric',
                                minute: '2-digit',
                              })}
                          </div>
                        </div>
                        <div>{interview.warehouseOrDepot.name}</div>
                        <div>{interview.phone ? 'Phone' : 'Onsite'}</div>
                        <div>{interview.interviewer?.name && `Interviewer: ${interview.interviewer.name}`}</div>
                        <div>{interview.passedAt ? '✓ Passed' : interview.failedAt ? '✗ Failed' : ''}</div>
                        {interview.noShow && <div>No Show</div>}
                        {index < application.interviews.length - 1 && <hr />}
                      </div>
                    ))
                  ) : application.status === Hiring__JobApplicationStatusEnum.GoogleSchedulerProvided ? (
                    <div>Via Google Calendar</div>
                  ) : (
                    <div>Not Scheduled</div>
                  )}
                </td>
                <td>
                  <span className={`badge ${getStatusBadgeClass(application.status)}`}>
                    {getStatusDisplayText(application.status)}
                  </span>
                </td>
                <td>
                  <NavLinkButton to={`/hiring/job_applications/${application.id}/review`} kind="primary">
                    Review
                  </NavLinkButton>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        {paginated && <Pagination pagination={paginated.pagination} onPage={setPage} />}
        {loading && <Spinner />}
      </Panel>
    </>
  );
};
